// apps/web/src/pages/planejamento/kanban/kanban-cartao-painel.tsx — Fase 11, Plano 17 (KAN-02, D-31, D-48, D-50).
// Painel lateral do cartão: aba "Detalhes" (reaproveita OsDetalhePanel) e aba "Histórico" (só PCP/Admin).
import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import type { KanbanCartao, KanbanHistoricoItem, OsDetalheResponse } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { OsDetalhePanel } from '../os-detalhe-panel';
import { filtrarHistoricoTardio, formatarPrazoCompleto, origemDoRegistroTardio, rotuloEstado } from '../../../lib/kanban/apresentacao';
import { apresentarAlertaCancelamento } from '../../../lib/pcp/alertas-cancelamento';
import { Eye } from 'lucide-react';
import { EncerrarOpParcialDialog } from './encerrar-op-parcial-dialog';
import { podeEncerrarOpParcial, rotaCartaoControle } from './encerrar-op-parcial';

export const TEXTO_ERRO_PAINEL = 'Não foi possível carregar os dados do cartão.';
export const TEXTO_HISTORICO_VAZIO = 'Nenhum evento registrado ainda.';

type EstadoCarga<T> =
  | { tipo: 'carregando' }
  | { tipo: 'erro' }
  | { tipo: 'ok'; dados: T };

/** Busca JSON de `url` quando `ativo`; `tentar` refaz a chamada após erro. */
function useCarga<T>(url: string | null, ativo: boolean): [EstadoCarga<T>, () => void] {
  const [estado, setEstado] = useState<EstadoCarga<T>>({ tipo: 'carregando' });
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    if (!ativo || !url) return;
    let vivo = true;
    setEstado({ tipo: 'carregando' });
    void (async () => {
      try {
        const res = await fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(String(res.status));
        const dados = (await res.json()) as T;
        if (vivo) setEstado({ tipo: 'ok', dados });
      } catch {
        if (vivo) setEstado({ tipo: 'erro' });
      }
    })();
    return () => {
      vivo = false;
    };
  }, [url, ativo, tentativa]);

  return [estado, () => setTentativa((n) => n + 1)];
}

function Esqueleto() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-label="Carregando">
      <Skeleton className="h-12 w-full rounded-md" />
      <Skeleton className="h-12 w-full rounded-md" />
      <Skeleton className="h-12 w-full rounded-md" />
    </div>
  );
}

function ErroCarga({ onTentar }: { onTentar: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700"
    >
      <AlertCircle className="h-6 w-6 text-red-500" aria-hidden="true" />
      <p>{TEXTO_ERRO_PAINEL}</p>
      <Button variant="outline" size="sm" onClick={onTentar} className="gap-2">
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
        Tentar novamente
      </Button>
    </div>
  );
}

/** Motivo textual registrado no evento (bloqueio, pausa, prioridade...), quando existir. */
export function motivoDoEvento(item: Pick<KanbanHistoricoItem, 'after'>): string | null {
  const depois = item.after;
  if (!depois || typeof depois !== 'object' || Array.isArray(depois)) return null;
  const registro = depois as Record<string, unknown>;
  for (const chave of ['motivo', 'justificativa', 'observacao']) {
    const valor = registro[chave];
    if (typeof valor === 'string' && valor.trim().length > 0) return valor.trim();
  }
  return null;
}

function formatarDataHora(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function MotivoExpansivel({ texto }: { texto: string }) {
  const [aberto, setAberto] = useState(false);
  return (
    <div className="mt-1 text-xs text-slate-600">
      <p className={`whitespace-pre-wrap break-words ${aberto ? '' : 'line-clamp-2'}`}>{texto}</p>
      {texto.length > 80 && (
        <button
          type="button"
          onClick={() => setAberto((v) => !v)}
          className="mt-0.5 font-medium text-blue-700 hover:underline"
        >
          {aberto ? 'Ver menos' : 'Ver mais'}
        </button>
      )}
    </div>
  );
}

function ItemHistorico({ item }: { item: KanbanHistoricoItem }) {
  const motivo = motivoDoEvento(item);
  const origem = origemDoRegistroTardio(item);
  return (
    <li className="rounded-md border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-slate-800">{item.rotulo}</span>
        <time className="shrink-0 text-[11px] text-slate-500" dateTime={item.createdAt}>
          {formatarDataHora(item.createdAt)}
        </time>
      </div>
      <p className="mt-0.5 text-xs text-slate-500">
        {item.actorNome ?? 'Sistema'}
        {item.deviceId ? ` · Dispositivo ${item.deviceId}` : ''}
      </p>
      {origem && <p className="mt-0.5 text-xs font-medium text-slate-700">{origem}</p>}
      {motivo && <MotivoExpansivel texto={motivo} />}
      {item.avisosIgnorados.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1" aria-label="Avisos ignorados">
          {item.avisosIgnorados.map((aviso) => (
            <li
              key={aviso}
              className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-900"
            >
              Aviso ignorado: {aviso}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function AbaHistorico({ osId, ativo }: { osId: string | null; ativo: boolean }) {
  const url = osId ? `/api/v1/kanban/cartoes/${encodeURIComponent(osId)}/historico` : null;
  const [estado, tentar] = useCarga<KanbanHistoricoItem[]>(url, ativo);
  const [apenasTardios, setApenasTardios] = useState(false);

  if (!osId) return <p className="text-sm text-slate-500">{TEXTO_HISTORICO_VAZIO}</p>;
  if (estado.tipo === 'carregando') return <Esqueleto />;
  if (estado.tipo === 'erro') return <ErroCarga onTentar={tentar} />;
  if (estado.dados.length === 0) return <p className="text-sm text-slate-500">{TEXTO_HISTORICO_VAZIO}</p>;
  const itens = filtrarHistoricoTardio(estado.dados, apenasTardios);
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        aria-pressed={apenasTardios}
        onClick={() => setApenasTardios((v) => !v)}
        className={`h-8 self-start rounded-full border px-3 text-xs font-medium transition-colors ${
          apenasTardios ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300'
        }`}
      >
        Só registros tardios
      </button>
      {itens.length === 0 ? (
        <p className="text-sm text-slate-500">Nenhum registro tardio neste cartão.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {itens.map((item) => (
            <ItemHistorico key={item.id} item={item} />
          ))}
        </ul>
      )}
    </div>
  );
}

function DadosDaOp({ cartao }: { cartao: KanbanCartao }) {
  return (
    <dl className="grid grid-cols-2 gap-2 rounded-lg border bg-slate-50 p-3 text-xs">
      <div>
        <dt className="text-[11px] text-slate-500">Quantidade</dt>
        <dd className="font-semibold text-slate-800">
          {cartao.quantidadeProduzida} / {cartao.quantidadeAlvo}
        </dd>
      </div>
      <div>
        <dt className="text-[11px] text-slate-500">Prazo</dt>
        <dd className="font-semibold text-slate-800">{formatarPrazoCompleto(cartao.prazo)}</dd>
      </div>
      <div>
        <dt className="text-[11px] text-slate-500">Estado</dt>
        <dd className="font-semibold text-slate-800">{rotuloEstado(cartao.estado).texto}</dd>
      </div>
      <div>
        <dt className="text-[11px] text-slate-500">Célula</dt>
        <dd className="font-semibold text-slate-800">{cartao.celulaCodigo ?? 'Sem célula'}</dd>
      </div>
    </dl>
  );
}

function AbaDetalhes({ cartao, ativo, onVerDesenho }: { cartao: KanbanCartao; ativo: boolean; onVerDesenho?: (osId: string) => void }) {
  const url = cartao.osId ? `/api/v1/planejamento/os/${encodeURIComponent(cartao.osId)}` : null;
  const [estado, tentar] = useCarga<OsDetalheResponse>(url, ativo);

  // Cartões da Fila de entrada (sem OS) mostram só os dados da OP.
  if (!cartao.osId) return <DadosDaOp cartao={cartao} />;
  if (estado.tipo === 'carregando') return <Esqueleto />;
  if (estado.tipo === 'erro') return <ErroCarga onTentar={tentar} />;
  return (
    <div className="flex flex-col gap-4">
      <DadosDaOp cartao={cartao} />
      {cartao.osId && onVerDesenho && (
        <button
          type="button"
          onClick={() => onVerDesenho(cartao.osId ?? '')}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded border border-slate-300 px-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
        >
          <Eye className="size-4" aria-hidden="true" />Ver desenho da revisão usada
        </button>
      )}
      <OsDetalhePanel os={estado.dados} onClose={() => undefined} embutido />
    </div>
  );
}

interface KanbanCartaoPainelProps {
  cartao: KanbanCartao | null;
  /** Permissão `kanban:gerenciar` — só ela vê a aba Histórico (D-50). */
  podeGerenciar: boolean;
  onFechar: () => void;
  onVerDesenho?: (osId: string) => void;
  podeEncerrarParcial: boolean;
  podeConsultarProducao: boolean;
  onRecarregarQuadro: () => void;
}

export function KanbanCartaoPainel({ cartao, podeGerenciar, onFechar, onVerDesenho, podeEncerrarParcial, podeConsultarProducao, onRecarregarQuadro }: KanbanCartaoPainelProps) {
  const [aba, setAba] = useState('detalhes');
  const [encerrandoParcialmente, setEncerrandoParcialmente] = useState(false);

  useEffect(() => {
    setAba('detalhes');
  }, [cartao?.chave]);

  const aoMudarAberto = useCallback(
    (aberto: boolean) => {
      if (!aberto) onFechar();
    },
    [onFechar],
  );
  const rotaCp = cartao ? rotaCartaoControle(cartao.loteId, cartao.opId, podeConsultarProducao) : null;

  return (
    <Sheet open={cartao !== null} onOpenChange={aoMudarAberto}>
      <SheetContent side="right" className="w-full gap-0 overflow-y-auto sm:max-w-md">
        {cartao && (
          <>
            <SheetHeader className="border-b bg-slate-50 pr-10">
              <span className="font-mono text-[11px] font-semibold uppercase text-slate-500">
                Lote {cartao.loteCodigo} · OP {cartao.opNumero}
              </span>
              <SheetTitle className="text-base font-bold text-slate-900 break-words">
                {cartao.clienteNome}
              </SheetTitle>
              <SheetDescription className="text-slate-700 break-words">
                {cartao.itemCodigo} — {cartao.itemNome}
              </SheetDescription>
              {apresentarAlertaCancelamento(cartao.alertaCancelamentoFiscal === true) && (
                <a href="/pcp/recebimentos?aba=pendencias" className="mt-2 flex min-h-11 items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-950">
                  <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
                  <span>{apresentarAlertaCancelamento(cartao.alertaCancelamentoFiscal === true)?.mensagem}</span>
                </a>
              )}
            </SheetHeader>
            <Tabs value={aba} onValueChange={setAba} className="p-4">
              {podeGerenciar && (
                <TabsList>
                  <TabsTrigger value="detalhes">Detalhes</TabsTrigger>
                  <TabsTrigger value="historico">Histórico</TabsTrigger>
                </TabsList>
              )}
              {rotaCp && (
                <a className="mb-3 inline-flex min-h-11 items-center text-sm font-medium text-primary underline-offset-4 hover:underline" href={rotaCp}>Cartão de Controle</a>
              )}
              {podeEncerrarOpParcial(cartao.opStatus, podeEncerrarParcial) && (
                <Button type="button" variant="outline" className="mb-3" onClick={() => setEncerrandoParcialmente(true)}>Encerrar parcialmente</Button>
              )}
              <TabsContent value="detalhes">
                <AbaDetalhes cartao={cartao} ativo={aba === 'detalhes'} onVerDesenho={onVerDesenho} />
              </TabsContent>
              {podeGerenciar && (
                <TabsContent value="historico">
                  <AbaHistorico osId={cartao.osId} ativo={aba === 'historico'} />
                </TabsContent>
              )}
            </Tabs>
            <EncerrarOpParcialDialog aberto={encerrandoParcialmente} onAbertoChange={setEncerrandoParcialmente} opId={cartao.opId} produzido={cartao.quantidadeProduzida} pendente={Math.max(0, cartao.quantidadeAlvo - cartao.quantidadeProduzida)} onSucesso={() => { onRecarregarQuadro(); onFechar(); }} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
